import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState } from '../../../redux/reducers';
import { formatDate } from '../../../utils/stringUtils';
import {
  BankTransactionParticipation,
  CreateBankTransactionModel,
  TransactionType,
  BankTransactionWithParticipation,
} from '../../../types/bankAccount';
import Button from '../buttons/Button';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import ModalLayout from '../layouts/ModalLayout';
import Labeled from '../misc/Labeled';
import Input from '../misc/Input';
import DropdownSelect from '../forms/DropdownSelect';

import SplitMembersList from '../financial/SplitMembersList';
import DatePicker from '../forms/DatePicker';
import TransactionProcessor from '../financial/TransactionProcessor';

// Helper function to convert BankTransactionWithParticipation to CreateBankTransactionModel
function convertToCreateModel(
  transaction: BankTransactionWithParticipation,
): CreateBankTransactionModel {
  return {
    amount: transaction.amount,
    description: transaction.description,
    date: transaction.date,
    mutationId: transaction.mutationId,
    payerId: transaction.payerId,
    transactionType: transaction.transactionType,
    participations: transaction.participations || undefined,
    contributionPayment: transaction.contributionPayment,
  };
}

interface DeclarationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialTransaction?: CreateBankTransactionModel | BankTransactionWithParticipation;
  onSplitSuccess?: (transaction: CreateBankTransactionModel) => void;
}

type DeclarationState = {
  description: string;
  date: Date;
  amount: number;
  transactionType?: TransactionType;
  payerId?: number;
  mutationId?: number;
  selectedMembers: number[];
  memberValues: Record<number, number>; // store absolute values in cents
  memberRations?: Record<number, number | undefined>;
  contributionPayment?: {
    contributionPlanId?: number;
    connectionId?: number;
  };
  isContributionPayment?: boolean;
};

const EMPTY_DECLARATION_STATE = {
  description: '',
  date: new Date(),
  amount: 0,
  selectedMembers: [],
  memberValues: {},
  memberRations: {},
};

const transformTransactionModelIntoState = (tns: CreateBankTransactionModel) => {
  return {
    ...tns,
    contributionPayment: tns.contributionPayment ?? undefined,
    selectedMembers: tns.participations?.map((p) => p.connectionId) ?? [],
    memberValues:
      tns.participations?.reduce(
        (acc, p) => {
          acc[p.connectionId] = p.amount;
          return acc;
        },
        {} as Record<number, number>,
      ) ?? {},
  };
};

export default function DeclarationModal({
  open,
  setOpen,
  onSplitSuccess,
  initialTransaction,
}: DeclarationModalProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.splitModal' });
  const { t: tEnum } = useTranslation('', { keyPrefix: 'page.financial.enums' });
  const connectionsAll = useSelector((state: RootState) => state.connections);
  const connections = connectionsAll.filter((c) => c.type === ConnectionType.B2C_CONNECTION);
  const [declaration, setDeclaration] = useState<DeclarationState>({
    ...EMPTY_DECLARATION_STATE,
  });
  const [remainingAmount, setRemainingAmount] = useState(initialTransaction?.amount ?? 0);

  const editMode = useMemo(() => !!initialTransaction, [initialTransaction]);

  useEffect(() => {
    if (!initialTransaction) {
      setDeclaration({ ...EMPTY_DECLARATION_STATE });
      setRemainingAmount(0);
    } else {
      const transactionToUse =
        'transactionId' in initialTransaction
          ? convertToCreateModel(initialTransaction)
          : initialTransaction;
      setDeclaration(transformTransactionModelIntoState(transactionToUse));
      setRemainingAmount(initialTransaction.amount);
    }
  }, [initialTransaction]);

  useEffect(() => {
    if (
      declaration.transactionType &&
      (declaration.transactionType.startsWith('GROUP') ||
        declaration.transactionType === TransactionType.CONTRIBUTION)
    ) {
      setDeclaration((prevTns) => ({
        ...prevTns,
        selectedMembers: [],
        memberValues: {},
        memberRations: {},
      }));
      setRemainingAmount(0);
    }
  }, [declaration.transactionType]);

  // Rations-based recalculation similar to AssignSplitTransactionModal
  const recalcFromRations = (
    prev: DeclarationState,
    override?: {
      memberValues?: Record<number, number>;
      memberRations?: Record<number, number | undefined>;
    },
  ): DeclarationState => {
    const totalAbs = Math.abs(prev.amount);
    const memberValues = override?.memberValues ?? prev.memberValues ?? {};
    const memberRations = override?.memberRations ?? prev.memberRations ?? {};

    const manualIds = Object.keys(memberValues)
      .map((id) => +id)
      .filter((id) => memberRations[id] === undefined);
    const manualAbsSum = manualIds.reduce((sum, id) => sum + Math.abs(memberValues[id] || 0), 0);
    const remainingAbs = Math.max(0, totalAbs - manualAbsSum);

    const rationEntries = Object.entries(memberRations)
      .map(([k, v]) => ({ id: +k, r: v }))
      .filter((e) => e.r !== undefined);
    const sumRations = rationEntries.reduce((sum, e) => sum + (e.r || 0), 0);

    const newMemberValues: Record<number, number> = { ...memberValues };
    if (sumRations > 0 && remainingAbs > 0) {
      let allocatedAbs = 0;
      rationEntries.forEach((e) => {
        const r = e.r || 0;
        if (r <= 0) {
          newMemberValues[e.id] = 0;
        } else {
          const shareAbs = Math.floor((remainingAbs * r) / sumRations);
          newMemberValues[e.id] = shareAbs;
          allocatedAbs += shareAbs;
        }
      });
      let remainder = remainingAbs - allocatedAbs;
      rationEntries.some((e) => {
        if (remainder <= 0) return true;
        if ((e.r || 0) > 0) {
          newMemberValues[e.id] = (newMemberValues[e.id] || 0) + 1;
          remainder -= 1;
        }
        return false;
      });
    } else {
      rationEntries.forEach((e) => {
        if ((e.r || 0) === 0) newMemberValues[e.id] = 0;
      });
    }

    return { ...prev, memberValues: newMemberValues, memberRations };
  };

  useEffect(() => {
    if (declaration.transactionType && declaration.transactionType.startsWith('PERSONAL')) {
      const totalAssignedAbs = Object.values(declaration.memberValues).reduce(
        (sum, value) => sum + Math.abs(value || 0),
        0,
      );
      setRemainingAmount(Math.abs(declaration.amount) - totalAssignedAbs);
    }
  }, [declaration.transactionType, declaration.memberValues, declaration.amount]);

  // Handle member value change (clamped, sets member to manual)
  const handleValueChange = (memberId: number, value: number) => {
    setDeclaration((prev) => {
      const valueInCentsAbs = Math.max(0, Math.floor((value || 0) * 100));
      const memberRations = { ...(prev.memberRations ?? {}) };
      memberRations[memberId] = undefined;

      const pre = recalcFromRations(prev, { memberRations });
      const otherMembersTotalAbs = Object.entries(pre.memberValues)
        .filter(([id]) => +id !== memberId)
        .reduce((sum, [, v]) => sum + Math.abs(v || 0), 0);
      const maxForThisMemberAbs = Math.max(0, Math.abs(prev.amount) - otherMembersTotalAbs);
      const clampedAbs = Math.min(maxForThisMemberAbs, valueInCentsAbs);

      const updatedMemberValues = {
        ...pre.memberValues,
        [memberId]: clampedAbs,
      };

      return recalcFromRations(
        { ...pre, memberValues: updatedMemberValues },
        {
          memberValues: updatedMemberValues,
          memberRations,
        },
      );
    });
  };

  const handleAmountChange = (stringAmount: string) => {
    const amount = parseFloat(stringAmount);

    setDeclaration((prevTns) => ({
      ...prevTns,
      transactionType: undefined,
      amount: amount ? -Math.abs(Math.floor(amount * 100)) : NaN,
    }));
  };

  const splitEqually = () => {
    setDeclaration((prev) => {
      const selected = connections.map((m) => +m.id);
      if (selected.length === 0) return prev;
      const totalAbs = Math.abs(prev.amount);
      const equalAbs = Math.floor(totalAbs / selected.length);
      const remainder = totalAbs % selected.length;
      const memberValues: Record<number, number> = {};
      const memberRations: Record<number, number | undefined> = {};
      selected.forEach((id, index) => {
        memberValues[id] = equalAbs + (index < remainder ? 1 : 0);
        memberRations[id] = undefined;
      });
      return { ...prev, selectedMembers: selected, memberValues, memberRations };
    });
  };

  const resetSplit = () => {
    setDeclaration((prev) => ({
      ...prev,
      selectedMembers: [],
      memberValues: {},
      memberRations: {},
    }));
  };

  const isResetable = useMemo(
    () => Object.values(declaration.memberValues).some((v) => (v || 0) > 0),
    [declaration.memberValues],
  );

  const handleSave = () => {
    let memberSplits: BankTransactionParticipation[] | undefined;
    if (declaration.transactionType?.startsWith('PERSONAL')) {
      const sign = declaration.amount >= 0 ? 1 : -1;
      const participatingMembers = Object.entries(declaration.memberValues)
        .filter(([, value]) => (value || 0) > 0)
        .map(([id]) => +id);
      if (participatingMembers.length === 0) return;
      memberSplits = participatingMembers.map((memberId) => ({
        connectionId: memberId,
        amount: sign * (declaration.memberValues[memberId] || 0),
      }));
    }

    if (!declaration.transactionType || Number.isNaN(declaration.amount)) return;
    if (!declaration.payerId) return;
    if (
      declaration.transactionType === TransactionType.CONTRIBUTION &&
      (!declaration.contributionPayment ||
        !declaration.contributionPayment.connectionId ||
        !declaration.contributionPayment.contributionPlanId)
    )
      return;

    const declarationDto: CreateBankTransactionModel = {
      amount: declaration.amount,
      description: declaration.description,
      date: declaration.date,
      mutationId: declaration.mutationId,
      payerId: declaration.payerId,
      transactionType: declaration.transactionType,
      participations: declaration.transactionType.startsWith('PERSONAL') ? memberSplits : undefined,
      contributionPayment:
        declaration.transactionType === TransactionType.CONTRIBUTION
          ? {
              contributionPlanId: declaration.contributionPayment!.contributionPlanId!,
              connectionId: declaration.contributionPayment!.connectionId!,
            }
          : undefined,
    };

    onSplitSuccess?.(declarationDto);
  };

  // No checkbox selection; SplitMembersList manages values/rations

  if (!declaration) {
    return null;
  }

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-2xl rounded-lg" closeButton>
      <div className="max-h-[calc(100vh-5rem)] overflow-y-auto rounded-md bg-secondary-50 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-semibold">
            {editMode ? t('editDeclaration') : t('addDeclaration')}
          </h3>
        </div>

        {/* Processor Information */}
        {initialTransaction &&
          'processedByConnection' in initialTransaction &&
          'transactionId' in initialTransaction && (
            <div className="mb-4 rounded-lg bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-500">{t('processedBy')}:</p>
                <TransactionProcessor transaction={initialTransaction} />
              </div>
            </div>
          )}

        <div className="mb-4">
          <div className="mb-2 flex flex-row gap-4">
            <div className="flex-1">
              <Labeled label={t('description')} htmlFor="declaration-description-textarea">
                <textarea
                  className="h-[unset] w-full"
                  rows={2}
                  id="declaration-description-textarea"
                  placeholder={t('descriptionPlaceholder')}
                  value={declaration.description}
                  onChange={(ev) =>
                    setDeclaration((prevTns) => ({
                      ...prevTns,
                      description: ev.target.value,
                    }))
                  }
                />
              </Labeled>
              <Labeled label={t('date')}>
                <DatePicker
                  disabled={false}
                  className="h-[unset] w-full"
                  date={declaration.date}
                  includeTime={false}
                  setDate={(newDate) =>
                    setDeclaration((prevTns) => ({
                      ...prevTns,
                      date: newDate ?? new Date(),
                    }))
                  }
                />
                <p className="pb-1 text-sm text-opacity-80">{formatDate(declaration.date)}</p>
              </Labeled>
            </div>
            <Labeled label={t('amount')} className="ml-2 flex max-w-36">
              <div className="relative flex gap-1">
                <p className="absolute left-2 top-[25px] -translate-y-1/2 text-lg font-medium">
                  &euro;
                </p>
                <Input
                  type="number"
                  className="pl-6"
                  error={Number.isNaN(declaration.amount) ? t('invalidAmount') : undefined}
                  defaultValue={
                    declaration.amount === 0
                      ? undefined
                      : Math.abs(declaration.amount / 100).toFixed(2)
                  }
                  placeholder="10.99"
                  onChange={(e) => handleAmountChange(e.target.value)}
                />
              </div>
            </Labeled>
          </div>

          <Labeled label={t('paidBy')}>
            <DropdownSelect
              options={connections.map((c) => ({
                label: `${c.fields.FIRST_NAME} ${c.fields.LAST_NAME}`,
                value: +c.id,
              }))}
              setValue={(payerId) =>
                setDeclaration((prevTns) => ({
                  ...prevTns,
                  payerId,
                }))
              }
              value={declaration.payerId}
            />
          </Labeled>

          <div className="flex items-center justify-center gap-2 pl-2">
            {[TransactionType.GROUP_EXPENSE, TransactionType.PERSONAL_EXPENSE].map((tt) => {
              return (
                <button
                  className={`my-2 rounded-full border-2 border-primary-300 bg-primary-300 px-4 py-2 transition-all ${tt === declaration.transactionType ? 'bg-opacity-75 font-medium' : 'bg-opacity-25'}`}
                  type="button"
                  onClick={() =>
                    setDeclaration((prevTns) => ({
                      ...prevTns,
                      transactionType: tt,
                    }))
                  }
                  key={tt}>
                  {tEnum(`TransactionType.${tt}`)}
                </button>
              );
            })}
          </div>

          {declaration.transactionType?.startsWith('PERSONAL') && (
            <SplitMembersList
              members={connections.map((m) => ({
                id: +m.id,
                label: `${m.fields.FIRST_NAME} ${m.fields.LAST_NAME}`,
              }))}
              memberValues={declaration.memberValues}
              memberRations={declaration.memberRations}
              onSetValue={(id, v) => handleValueChange(id, v)}
              onSetRations={(id, r) =>
                setDeclaration((prev) =>
                  recalcFromRations(prev, {
                    memberRations: { ...(prev.memberRations ?? {}), [id]: r },
                  }),
                )
              }
              headerTitle={t('selectMembers')}
              onSplit={splitEqually}
              onClear={resetSplit}
              isResetable={isResetable}
              remainingAmount={remainingAmount}
              remainingLabel={t('remaining')}
            />
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="primary-dark"
            className="bg-secondary-200 text-primary-900"
            onClick={() => setOpen(false)}>
            {t('cancelButton')}
          </Button>
          <Button
            variant="primary-dark"
            onClick={handleSave}
            disabled={
              !declaration.transactionType ||
              remainingAmount !== 0 ||
              !declaration.amount ||
              !declaration.payerId
            }>
            {t('assignButton')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
